import { memo, useEffect, useMemo, useRef, useState } from 'react';
import Highlighter from 'react-highlight-words';

import { Input, Typography } from 'antd';
import cn from 'classnames';
import { map } from 'lodash';
import { TCustomer } from 'types/customer';

import { Common } from '@classes/commonMethods';
import { GlobalConstants } from '@constants/global';
import RenderIf from '@common/RenderIf';
import { faRubleSign } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { columnViewStore } from '@molecules/listViewChanger/listViewStore';
import { getUserFullName } from '@utils/getUserFullName';
import { imagePicker } from '@utils/imagePicker';

import AutoResizeText from '@atoms/listsCard/autoResizeText/autoResizeText';
import useGetCardWidth from '@atoms/listsCard/hooks/useGetCardWidth';

import styles from './customerCard.module.scss';

const { Paragraph } = Typography;

type Props = {
    customer: TCustomer | null;
    searchQuery?: string;
    isCreating?: boolean;
    customerName?: string;
    onCustomerNameChange?: (name: string) => void;
};

const CustomerCard = memo(
    ({ 
        customer, 
        searchQuery = '', 
        isCreating = false,
        customerName = '',
        onCustomerNameChange 
    }: Props) => {
        const cardRef = useRef(null);
        const cardWidth = useGetCardWidth({ cardRef });
        const [localName, setLocalName] = useState(customerName);

        // Синхронизация локального состояния с внешним значением
        useEffect(() => {
            setLocalName(customerName);
        }, [customerName]);

        const avatar = useMemo(() => {
            if (isCreating || !customer) {
                return `${GlobalConstants.BaseUrlForImg}${GlobalConstants.NoImageWorkerUrl}`;
            }

            const normalizedPictures =
                (customer?.pictures?.length
                    ? map(customer.pictures, (pic) => imagePicker({ picture: pic }))
                    : null) ||
                (customer?.user?.pictures?.length
                    ? map(customer.user.pictures, (pic) => imagePicker({ picture: pic }))
                    : null);

            const normalizedPicture =
                imagePicker({ picture: customer?.picture }) ||
                imagePicker({ picture: customer?.user?.picture });

            return normalizedPictures?.[0] || normalizedPicture || `${GlobalConstants.BaseUrlForImg}${GlobalConstants.NoImageWorkerUrl}`;
        }, [customer, isCreating]);

        const handleNameChange = (value: string) => {
            setLocalName(value);
            onCustomerNameChange?.(value);
        };

        return (
            <div
                id={customer ? `customer-card-${customer.id}` : 'customer-card-creating'}
                ref={cardRef}
                className={cn(styles.card, {
                    [styles['card_vertical']]: columnViewStore.col > 1,
                })}
            >
                <div className={styles['card__image']}>
                    <img src={avatar} alt={isCreating ? 'Новый клиент' : getUserFullName(customer)} />
                </div>
                <div className={styles['card__info']}>
                    {isCreating || !customer ? (
                        <Input
                            className={styles['card__name-input']}
                            placeholder="Введите имя клиента"
                            value={localName}
                            onChange={(e) => handleNameChange(e.target.value)}
                            size="small"
                        />
                    ) : (
                        <Paragraph className={styles['card__smaller-margin']} ellipsis={{ rows: 1 }}>
                            <Highlighter
                                className="ant-typography"
                                searchWords={searchQuery.trim() ? searchQuery.trim().split(/\s+/) : []}
                                autoEscape
                                textToHighlight={getUserFullName(customer)}
                            />
                        </Paragraph>
                    )}

                    <RenderIf condition={customer?.balance}>
                        <div className={styles['card__price-container']}>
                            <AutoResizeText maxWidth={cardWidth}>
                                {Common.formatCurrency(customer?.balance)}
                                &nbsp;
                                <FontAwesomeIcon color="black" icon={faRubleSign} />
                            </AutoResizeText>
                        </div>
                    </RenderIf>
                </div>
            </div>
        );
    },
    (prevProps, nextProps) => {
        if (prevProps.isCreating !== nextProps.isCreating) return false;
        if (prevProps.customer?.id !== nextProps.customer?.id) return false;
        if (prevProps.searchQuery !== nextProps.searchQuery) return false;
        if (prevProps.customerName !== nextProps.customerName) return false;
        return true;
    }
);

CustomerCard.displayName = 'CustomerCard';

export { CustomerCard };

